import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Award, Zap, Shield, ArrowRight, Users, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { stats, companyInfo } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="About Arikone - Your Digital Marketing Partner in Noida"
        description="Learn about Arikone, a leading digital marketing agency in Noida with 15+ years of experience. Discover our mission, vision, values, and commitment to empowering businesses through strategic digital marketing."
        keywords="about arikone, digital marketing agency noida, marketing agency story, brand promotion experts, digital marketing team"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] rounded-full bg-blue-500/10 blur-3xl animate-pulse" />
          <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] rounded-full bg-purple-500/10 blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Content */}
            <div className="text-left animate-fadeInUp">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 mb-6 backdrop-blur-sm">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                Since 2010
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Driving <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Digital Excellence
                </span>
              </h1>

              <p className="text-xl text-blue-100 mb-8 max-w-xl leading-relaxed">
                Your dedicated partner in digital growth. We combine creativity, strategy, and technology to build brands that stand out and succeed.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-blue-600/25 transition-all hover:scale-105"
                >
                  <Link to="/contact">
                    Partner With Us <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-blue-400/30 text-blue-100 hover:bg-blue-900/50 hover:text-white px-8 py-6 text-lg rounded-full backdrop-blur-sm transition-all"
                >
                  Meet The Team
                </Button>
              </div>
            </div>

            {/* Right Visuals */}
            <div className="relative hidden lg:block h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-purple-600/20 rounded-full blur-3xl" />

              {/* Floating Icons Grid/Cloud */}
              <div className="relative w-full h-full">
                {/* Center Piece */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl shadow-2xl animate-float">
                  <div className="text-center">
                    <span className="block text-5xl mb-2">🤝</span>
                    <div className="text-2xl font-bold text-white">Trusted Partner</div>
                    <div className="text-sm text-blue-200">For Growth</div>
                  </div>
                </div>

                {/* Orbiting Elements */}
                <div className="absolute top-10 right-10 bg-blue-500/20 backdrop-blur-md p-4 rounded-xl border border-blue-400/30 animate-bounce delay-100 shadow-lg shadow-blue-500/20">
                  <Users className="text-blue-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-20 left-10 bg-purple-500/20 backdrop-blur-md p-4 rounded-xl border border-purple-400/30 animate-bounce delay-300 shadow-lg shadow-purple-500/20">
                  <Target className="text-purple-300 w-8 h-8" />
                </div>
                <div className="absolute top-20 left-0 bg-indigo-500/20 backdrop-blur-md p-4 rounded-xl border border-indigo-400/30 animate-bounce delay-500 shadow-lg shadow-indigo-500/20">
                  <Globe className="text-indigo-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-0 right-20 bg-cyan-500/20 backdrop-blur-md p-4 rounded-xl border border-cyan-400/30 animate-bounce delay-700 shadow-lg shadow-cyan-500/20">
                  <Zap className="text-cyan-300 w-8 h-8" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 animate-fadeInUp">
              <div className="inline-block px-4 py-2 rounded-full bg-blue-50 text-blue-600 font-semibold text-sm mb-6">
                Our Journey
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                From a Small Team to a <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Digital Powerhouse
                </span>
              </h2>

              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  {companyInfo.description}
                </p>
                <p>
                  Founded with a vision to democratize premium digital marketing services, Arikone started as a passionate team of three. Today, we've grown into a full-service agency serving businesses across {companyInfo.serviceAreas}, driven by our commitment to delivering measurable results.
                </p>
                <p>
                  We specialize in serving salons, real estate agencies, travel businesses, and coaching institutes. Each industry presents unique challenges, and we thrive on crafting tailored solutions that drive real business growth.
                </p>
              </div>

              <div className="mt-10 flex items-center gap-4">
                <div className="flex -space-x-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-12 h-12 rounded-full border-2 border-white bg-gray-200 overflow-hidden">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Team member" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-bold text-gray-900">25+ Experts</span> <br />
                  Dedicated to your success
                </div>
              </div>
            </div>

            {/* Right Visual (Image + Quote) */}
            <div className="order-1 lg:order-2 relative animate-fadeInUp stagger-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                {/* Floating Quote Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-xl shadow-lg border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Award className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-800 font-medium italic mb-2">
                        "Success isn't just about numbers; it's about the stories we build and the brands we elevate."
                      </p>
                      <div className="text-sm text-gray-500 font-bold">- Rahul Sharma, CEO</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements around image */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-100 rounded-full blur-3xl -z-10" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-100 rounded-full blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="hover-glow animate-fadeInUp">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="text-blue-600" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-lg text-gray-700">
                  To empower businesses with innovative digital strategies that deliver measurable results,
                  allowing them to focus on their core strengths while we expertly craft their success story.
                  We are committed to being more than just a service provider—we are your growth partner.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-glow animate-fadeInUp stagger-1">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
                  <Eye className="text-yellow-600" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-lg text-gray-700">
                  To become the most trusted digital marketing agency in India, recognized for transforming
                  businesses through creative excellence, strategic innovation, and unwavering commitment to
                  client success. We envision a future where every brand can thrive in the digital landscape.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 animate-fadeInUp">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fadeInUp stagger-1">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Client-First Approach',
                description: 'Your success is our success. We prioritize your goals and work tirelessly to achieve them.'
              },
              {
                icon: Zap,
                title: 'Innovation',
                description: 'Staying ahead of trends and leveraging cutting-edge strategies to give you a competitive edge.'
              },
              {
                icon: Shield,
                title: 'Integrity',
                description: 'Transparent communication, honest advice, and ethical practices in everything we do.'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'Commitment to delivering the highest quality work that exceeds expectations.'
              },
              {
                icon: Target,
                title: 'Results-Driven',
                description: 'Data-backed strategies focused on measurable outcomes and ROI.'
              },
              {
                icon: Heart,
                title: 'Collaboration',
                description: 'Working closely with you as partners to create campaigns that truly represent your brand.'
              }
            ].map((value, index) => (
              <Card key={index} className={`hover-glow animate-fadeInUp stagger-${index + 1}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <value.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Arikone by the Numbers</h2>
            <p className="text-xl text-blue-100">Our growth reflects the success of our clients</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className={`text-center animate-fadeInUp stagger-${index + 1}`}>
                <div className="text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-blue-100 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Where We Are</h2>
          <p className="text-xl text-gray-600 mb-8">
            Based in Noida, serving businesses across Delhi, NCR, and all of India
          </p>
          <Card className="hover-glow">
            <CardContent className="p-8">
              <div className="space-y-2 text-left">
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Address:</span> {companyInfo.address}
                </p>
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Phone:</span> {companyInfo.phone}
                </p>
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Hours:</span> {companyInfo.hours}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;